import apiClient, { setAccessToken, setNativeSessionScope } from './client';
import type { ApiResponse, AuthTokens, AuthUser, LoginRequest, RegisterRequest } from '../types';
import { beginSessionTransition, serializeSessionRequest, sessionGeneration, sessionIsCurrent } from './authSession';

export interface RegisterResult {
  tokens: AuthTokens | null;
  user: AuthUser;
  requires_approval?: boolean;
  requires_email_verification?: boolean;
}

// A refresh token is consumed on first use. React StrictMode can mount the
// provider effect twice in development, and other callers can overlap a
// bootstrap refresh. Share the same in-flight request rather than replaying
// one cookie and treating the resulting 401 as a lost session.
let refreshInFlight: Promise<AuthTokens> | null = null;
let refreshOwner = -1;

export const authApi = {
  async login(data: LoginRequest): Promise<{ tokens: AuthTokens; user: AuthUser }> {
    const owner = beginSessionTransition();
    setAccessToken(null);
    setNativeSessionScope(null);
    const res = await serializeSessionRequest(owner, () => apiClient.post<ApiResponse<{ tokens: AuthTokens; user: AuthUser }>>(
      '/vendor/auth/login',
      data
    ));
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setAccessToken(res.data.data.tokens.access_token);
    // GROC-02: the same native session scope the mobile apps have used
    // since NAT-01 -- not yet in the AuthTokens type this response reuses.
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    setNativeSessionScope((res.data.data as any).native_session_scope ?? null);
    return res.data.data;
  },

  async register(data: RegisterRequest): Promise<RegisterResult> {
    const owner = beginSessionTransition();
    setAccessToken(null);
    setNativeSessionScope(null);
    const res = await serializeSessionRequest(owner, () => apiClient.post<ApiResponse<RegisterResult>>(
      '/vendor/auth/register',
      data
    ));
    if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
    // Tokens may be null when the vendor needs to verify email or
    // wait for admin approval — in that case we do NOT auto-login.
    if (res.data.data.tokens?.access_token) {
      setAccessToken(res.data.data.tokens.access_token);
      if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
      setNativeSessionScope((res.data.data as any).native_session_scope ?? null);
    }
    return res.data.data;
  },

  refreshToken(): Promise<AuthTokens> {
    const owner = sessionGeneration();
    if (!refreshInFlight || refreshOwner !== owner) {
      refreshOwner = owner;
      refreshInFlight = serializeSessionRequest(owner, () => apiClient.post<ApiResponse<AuthTokens>>('/vendor/auth/refresh'))
        .then((res) => {
          if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
          setAccessToken(res.data.data.access_token);
          if (!sessionIsCurrent(owner)) throw new Error('session_replaced');
          setNativeSessionScope((res.data.data as any).native_session_scope ?? null);
          return res.data.data;
        })
        .finally(() => {
          if (refreshOwner === owner) refreshInFlight = null;
        });
    }
    return refreshInFlight;
  },

  async logout(): Promise<void> {
    const owner = beginSessionTransition();
    // Clear locally even if the logout request is slow or fails.
    setAccessToken(null);
    setNativeSessionScope(null);
    try {
      await serializeSessionRequest(owner, () => apiClient.post('/vendor/auth/logout'));
    } finally {
      if (sessionIsCurrent(owner)) {
        setAccessToken(null);
        setNativeSessionScope(null);
      }
    }
  },

  async forgotPassword(email: string, shopSlug: string): Promise<void> {
    await apiClient.post('/vendor/auth/forgot-password', { email, shop_slug: shopSlug });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/vendor/auth/reset-password', { token, new_password: password });
  },

  /** Wave-63: change password while logged in. */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/vendor/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },

  async verifyEmail(token: string): Promise<{ verified: boolean; email: string; status: string }> {
    const res = await apiClient.post<ApiResponse<{ verified: boolean; email: string; status: string }>>(
      '/vendor/auth/verify-email',
      { token }
    );
    return res.data.data;
  },

  async resendVerification(email: string, shopSlug: string): Promise<void> {
    await apiClient.post('/vendor/auth/resend-verification', { email, shop_slug: shopSlug });
  },
};
